import React, { useEffect, useRef, useState } from 'react'
import ChatHeader from './ChatHeader'
import ChatInput from './ChatInput'
import {
    getChat, getMessageDraft,
    removeAllPendingMessages,
    removeUnreadMessages
} from '../../../../store/actionsCreators/chatActions'
import { useAppDispatch, useAppSelector } from '../../../../hooks/redux'
import { useParams } from 'react-router'
import { type RootState } from '../../../../store/store'
import _ from 'lodash'
import { setChat, setCurrentMessage, setMessages } from '../../../../store/slices/chatSlice'
import MessagesSwitch from './messages/MessagesSwitch'
import { nanoid } from 'nanoid'
import ChatUtils from '../../../../functions/chats/ChatUtils'
import { setPage } from '../../../../store/slices/paginationSlice'
import ListLoader from '../../../../app/custom/loaders/ListLoader'

import { type IChatMessage } from '../../../../types'
import messageDatesAdapter from '../../../../functions/adapters/chat/messageDatesAdapter'
import { Link, useSearchParams } from 'react-router-dom'
import { blockUser } from '../../../../store/actionsCreators/userActions'
import { Spin } from 'antd'

import { placeholders } from '../../../../urls'
import Avatar from '../../elements/Avatar'
import SupportStatusPanel from './SupportStatusPanel'
import { getSupportMarker } from '../../../../lib/helpers/support/supportTicketPresentation'

const PaginationLoader = (): JSX.Element => {
    const paginationLoading = useAppSelector((state: RootState) => state.pagination.paginationLoading)
    return (
        paginationLoading
            ? <div className={'w-100 d-flex justify-content-start position-absolute'} style={{ top: '5rem' }}>
                <Spin size={'default'} />
            </div>
            : <></>
    )
}

interface NewChatV2Props {
    isDesktop?: boolean
    readOnly?: boolean
    isAdminMode?: boolean
}

const NewChatV2 = ({ isDesktop = false, readOnly = false, isAdminMode = false }: NewChatV2Props): JSX.Element => {
    const bottomRef = useRef<HTMLDivElement>(null)
    const scrollRef = useRef<HTMLDivElement>(null)
    const scrollPageRef = useRef<HTMLDivElement>(null)
    const dispatch = useAppDispatch()
    const { id: chatID = '0' } = useParams()
    const currentUser = useAppSelector((state: RootState) => state.user.currentUser)
    const chat = useAppSelector((state: RootState) => state.chat.chat)
    const pendingMessages = useAppSelector((state: RootState) => state.chat.pendingMessages)
    const Messages = useAppSelector((state: RootState) => state.chat.Messages)
    const [searchParams, setSearchParams] = useSearchParams()
    // Do not use ChatUtils.isBSOSupport here: for an admin, the other participant is the client.
    // SupportTickets is the source of truth; markers only extend legacy timeline compatibility.
    const hasSupportMarkers = [...pendingMessages, ...Messages].some(message => getSupportMarker(message.Text) != null)
    const isAdminSupportView = isAdminMode && (chat.SupportTickets != null || hasSupportMarkers)
    const messageID = searchParams.get('message')
    const [loading, setLoading] = useState(true)
    const [blockLoading, setBlockLoading] = useState(false)




    useEffect(() => {
        //  console.log('COMPONENT MOUNT', [messageID, JSON.stringify((currentUser as any).BlackList), chatID])
        setLoading(true)
        dispatch(setPage(1))
        dispatch(setMessages([]))
        dispatch(setCurrentMessage(getMessageDraft(+chatID)))
        dispatch(getChat(+chatID, 1, messageID != null ? +messageID : undefined, undefined, () => {
            dispatch(setMessages([]))
            if (messageID == null) {
                dispatch(removeUnreadMessages(+chatID))
            }
        }))
            .finally(() => {
                setLoading(false);
                setTimeout(() => {
                    bottomRef?.current?.scrollIntoView({ behavior: 'smooth' });
                }, 100);
            });
        return () => {
            if (messageID == null) {
                dispatch(removeUnreadMessages(+chatID))
                dispatch(removeAllPendingMessages())
                dispatch(setCurrentMessage(''))
            }
            dispatch(setMessages([]))
            dispatch(setChat({}))
        }
    }, [messageID, JSON.stringify((currentUser as any).BlackList), chatID])



    const messages = messageDatesAdapter(Messages, 'asc')

    const backgroundStyle = isDesktop
        ? {
            position: 'absolute' as const,
            inset: 0,
            background: 'url(/assets/media/chat-background.png) no-repeat center center',
            backgroundSize: 'cover',
            zIndex: 0
        }
        : {
            position: 'fixed' as const,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'url(/assets/media/chat-background.png) no-repeat center center',
            backgroundSize: 'cover',
            zIndex: -1
        }

    const chatTitle = ChatUtils.isBSOBroadcast(chat)
        ? 'BSO Broadcast'
        : ChatUtils.isBSOSupport(chat)
            ? 'BSO Support'
            : chat?.User?.Name

    const profileLink = `/landlord/profile/${Number(chat?.User?.id)}/chat?chatId=${String(chat?.id)}`


    return (
        <div className="d-flex flex-column" id={'chat-wrapper'}
            ref={scrollPageRef}
            style={{
                height: '100%',
                overflowY: 'hidden',
                position: isDesktop ? 'relative' : undefined
            }}>
            {isDesktop
                ? <div className="chats-desktop__topbar">
                    <div className="d-flex align-items-center justify-content-between px-6 py-4">
                        <div className="fw-bold fs-3 text-dark">{chatTitle}</div>
                        {chat.BlockedBy != null
                            ? <div className="badge badge-light-danger">Blocked</div>
                            : <></>
                        }
                        <div className={'d-flex gap-4 align-items-center'}>
                            {<>
                                {!loading
                                    ? <Link to={ChatUtils.isBSOBroadcast(chat) || ChatUtils.isBSOSupport(chat) ? '' : profileLink}>
                                        <Avatar
                                            image={chat?.User?.Photo}
                                            size={'40px'}
                                            isAdmin={(ChatUtils.isBSOBroadcast(chat) || ChatUtils.isBSOSupport(chat))}
                                        />
                                    </Link>
                                    : <></>
                                }
                            </>
                            }
                        </div>
                    </div>

                </div>
                : <ChatHeader chat={chat} loading={loading} scrollRef={scrollPageRef} key={'header-chat'} />
            }
            <div style={backgroundStyle}></div>


            <div className={`${isDesktop ? 'chats-desktop__messages w-100' : 'w-screen'} fs-4 d-flex flex-column ${isDesktop ? 'p-4' : 'p-4 py-12'} flex-grow-1`}
                style={{
                    position: 'relative',
                    zIndex: 1,
                    height: '100%'
                }}
            >
                {
                    [...pendingMessages, ...messages].length === 0 && !loading
                        ?
                        <div
                            className={`d-flex position-relative align-items-end ${isDesktop ? 'py-6 my-2' : 'py-16 my-6'}`}
                            style={{ height: '100%', left: '50%', transform: 'translateX(-50%)', zIndex: 1 }}
                        >
                            <img
                                className="w-75 object-fit-cover m-auto rounded-0 rounded-bottom opacity-100  mt-12"
                                src={placeholders.Messages}
                                alt={'liable_picture'}
                            />
                            <span className="fs-2x fs-lg-2 fw-bold text-white text-uppercase position-absolute text-center" style={{ bottom: '22%', left: '50%', transform: 'translateX(-50%)', textShadow: '1px 1px 10px #332d2dd9' }}>
                                No messages yet
                            </span>
                        </div>
                        :
                        <></>
                }

                <div
                    ref={scrollRef}
                    className={`d-flex flex-column-reverse gap-4 ${isDesktop ? 'py-4 my-2' : 'py-16 my-6'}`}
                    style={{ overflowY: isDesktop ? 'visible' : 'auto' }}
                >
                    <div className="h-25px"></div>
                    {loading
                        ? <ListLoader key={'loader-chat'} />
                        : <>
                            <div id={'bottom-ref-element'} ref={bottomRef} />
                            {
                                messageID != null
                                    ? <>
                                        <div className={'d-flex w-100 justify-content-center'}>
                                            <div onClick={() => {
                                                dispatch(setMessages([]))
                                                setSearchParams({ from: 'single-message' })
                                            }} className={'btn btn-bso btn-bso-sm'}
                                            >
                                                See other messages
                                            </div>
                                        </div>
                                        <div style={{ height: '15rem' }}></div>
                                    </>
                                    :
                                    <></>
                            }
                            {
                                _.map(pendingMessages, (message) => (
                                    <MessagesSwitch sentBy={'me'} message={message} key={nanoid()} isPending={true} showSupportMarkers={isAdminSupportView} />
                                ))
                            }
                            {
                                _.map(messages, (message) => (
                                    (message as { date: string }).date !== undefined
                                        ? <div className={'d-flex justify-content-center'}>
                                            <div className={'badge bg-white color-black padding-1 fs-6'}>{(message as { date: string }).date}</div>
                                        </div>
                                        : <MessagesSwitch message={message as IChatMessage} key={nanoid()} showSupportMarkers={isAdminSupportView} />
                                ))
                            }
                            <PaginationLoader key={nanoid()} />
                        </>
                    }
                </div>
            </div >

            {!readOnly && <div className={`${isDesktop ? 'chats-desktop__input' : 'fixed-bottom'} w-100`} style={{ bottom: 0, zIndex: '100' }}>
                {isAdminSupportView ? <SupportStatusPanel chat={chat} /> : <></>}
                {chat.BlockedBy != null
                    ?
                    <div className={'w-100 d-flex justify-content-between align-items-center p-4'}>
                        {
                            chat.BlockedBy === currentUser.id
                                ? <>
                                    <div className={'text-danger'}>Chat is blocked by You</div>
                                    <div className={'btn btn-sm btn-danger'}
                                        onClick={() => {
                                            if (!blockLoading) {
                                                setBlockLoading(true)
                                                void dispatch(blockUser(chat?.User?.id ?? -1, true))
                                                    .finally(() => {
                                                        setBlockLoading(false)
                                                    })
                                            }
                                        }}
                                    >
                                        {
                                            !blockLoading
                                                ? <>Unblock {chat?.User?.Name}</>
                                                : <span className="indicator-progress d-block">
                                                    Please wait...
                                                    <span className={'spinner-border spinner-border-sm align-middle ms-4'}></span>
                                                </span>
                                        }
                                    </div>
                                </>
                                : <div className={'badge badge-danger'}>Chat is blocked by {chat?.User?.Name}</div>
                        }
                    </div>
                    : <ChatInput key={nanoid()} bottomRef={bottomRef} isDesktop={isDesktop} />
                }
            </div>}
        </div>

    )
}

export default NewChatV2
